import { useField, useFormikContext } from 'formik';

import { ComboBoxOption, PaginatedAsyncComboBox } from '../cdl/ComboBox/ComboBox';
import { getContract } from '../common/api/clients/contracts.api';
import { queries } from '../common/api/queryKeys/queries';
import { translate } from '../common/helpers/translate.helper';

import { useDefaultContractOptions } from './useDefaultContractOptions';

interface FormikDefaultContractSelectProps {
    name: string;
    defaultSupplierId?: string;
    customerId?: string;
    vesselId?: string;
    label?: string;
    disabled?: boolean;
    markAsOptional?: boolean;
    onChange?: (contractId: string | null) => void;
}

export const FormikDefaultContractSelect = ({
    name,
    defaultSupplierId,
    customerId,
    vesselId,
    label = translate('vessel.form.defaultContract'),
    disabled,
    markAsOptional,
    onChange,
}: FormikDefaultContractSelectProps) => {
    const [field, meta, helpers] = useField<string | null>(name);
    const { validateOnChange } = useFormikContext();
    const { setError, setValue } = helpers;
    const { optionsQueryFn, optionsQueryKey } = useDefaultContractOptions({
        customerId,
        supplierId: defaultSupplierId,
        vesselId,
    });

    const loadSelectedContract = async (contractId: string): Promise<ComboBoxOption | null> => {
        const contract = await getContract(contractId);

        return {
            value: contract.id,
            label: contract.name,
        };
    };

    const selectContract = (contractId: string | null) => {
        setValue(contractId);
        if (!validateOnChange && meta.error) {
            setError(undefined);
        }
        onChange?.(contractId);
    };

    if (!defaultSupplierId) {
        return null;
    }

    return (
        <PaginatedAsyncComboBox
            value={field.value || null}
            onChange={selectContract}
            onBlur={() => helpers.setTouched(true)}
            optionsQueryKey={optionsQueryKey}
            optionsQueryFn={optionsQueryFn}
            selectedOptionQueryKey={(selectedValue) => queries.contracts.selectedComboBoxOption(selectedValue).queryKey}
            selectedOptionQueryFn={loadSelectedContract}
            disabled={disabled}
            label={label}
            markAsOptional={markAsOptional}
            isClearable
            negative={meta.touched && !!meta.error}
            caption={meta.touched && meta.error ? meta.error : undefined}
        />
    );
};
